import cssVariablesJson from '@/assets/cssVariables.json';

export type ConcreteColourPaletteId =
    'dark'
    | 'light'
    | 'midnightHighlight'
    | 'warmLight'
    | 'slatePurple'
    | 'cleanBlue';
export type AutomaticColourPaletteId =
    'systemPreference'
    | 'dayWarmth'
    | 'eveningDark';
export type ColourPaletteId = ConcreteColourPaletteId | AutomaticColourPaletteId;
export type ColourPaletteMode = 'dark' | 'light';
export type ThemeVariables = (typeof cssVariablesJson.theme)[keyof typeof cssVariablesJson.theme];

export interface IColourPaletteOption {
    id: ColourPaletteId;
    name: string;
    description?: string;
    isRecommended?: boolean;
    swatches: ReadonlyArray<string>;
}

export const DEFAULT_COLOUR_PALETTE_ID: ColourPaletteId = 'dark';
export const COLOUR_PALETTE_STORAGE_KEY = 'spark.colourPaletteId';
export const SYSTEM_PREFERENCE_COLOUR_PALETTE_ID: AutomaticColourPaletteId = 'systemPreference';

const CONCRETE_COLOUR_PALETTE_MODE_BY_ID: Record<ConcreteColourPaletteId, ColourPaletteMode> = {
    dark: 'dark',
    light: 'light',
    midnightHighlight: 'dark',
    warmLight: 'light',
    slatePurple: 'dark',
    cleanBlue: 'light',
};

const TIME_SENSITIVE_AUTOMATIC_PALETTE_IDS: ReadonlySet<string> = new Set([
    'dayWarmth',
    'eveningDark',
]);

function getConcretePaletteSwatches (paletteId: ConcreteColourPaletteId): ReadonlyArray<string> {
    const themeVariables = cssVariablesJson.theme[paletteId];
    return [
        themeVariables.surface.neutral0,
        themeVariables.colours.primary.colour,
        themeVariables.colours.secondary.colour,
    ];
}

function getPalettePreviewSwatches (
    surfacePaletteId: ConcreteColourPaletteId,
    colourPaletteId = surfacePaletteId,
    colourName: 'primary' | 'secondary' = 'primary',
): ReadonlyArray<string> {
    const surfaceThemeVariables = cssVariablesJson.theme[surfacePaletteId];
    const colourThemeVariables = cssVariablesJson.theme[colourPaletteId];

    return [
        surfaceThemeVariables.surface.neutral0,
        colourThemeVariables.colours[colourName].colour,
    ];
}

export const STATIC_COLOUR_PALETTE_OPTIONS: ReadonlyArray<IColourPaletteOption> = [
    {
        id: 'dark',
        name: 'Dark',
        isRecommended: true,
        swatches: getConcretePaletteSwatches('dark'),
    },
    {
        id: 'light',
        name: 'Light',
        swatches: getConcretePaletteSwatches('light'),
    },
    {
        id: 'midnightHighlight',
        name: 'Highlight',
        swatches: getConcretePaletteSwatches('midnightHighlight'),
    },
    {
        id: 'slatePurple',
        name: 'Purple',
        swatches: getConcretePaletteSwatches('slatePurple'),
    },
    {
        id: 'cleanBlue',
        name: 'Clean blue',
        swatches: getConcretePaletteSwatches('cleanBlue'),
    },
    {
        id: 'warmLight',
        name: 'Warm light',
        swatches: getConcretePaletteSwatches('warmLight'),
    },
];

export const AUTOMATIC_COLOUR_PALETTE_OPTIONS: ReadonlyArray<IColourPaletteOption> = [
    {
        id: SYSTEM_PREFERENCE_COLOUR_PALETTE_ID,
        name: 'System',
        description: 'Follows your computer light or dark appearance setting.',
        swatches: [
            ...getPalettePreviewSwatches('light'),
            ...getPalettePreviewSwatches('dark', 'dark', 'secondary'),
        ],
    },
    {
        id: 'dayWarmth',
        name: 'Time-based warm light',
        description: 'Uses the light theme during the day, then gradually warms the palette from afternoon into evening.',
        swatches: [
            ...getPalettePreviewSwatches('light'),
            ...getPalettePreviewSwatches('warmLight'),
        ],
    },
    {
        id: 'eveningDark',
        name: 'Evening dark',
        description: 'Uses time-based warm light during the day, then switches to Dark in the evening and overnight.',
        swatches: [
            ...getPalettePreviewSwatches('light', 'warmLight'),
            ...getPalettePreviewSwatches('dark'),
        ],
    },
];

export const COLOUR_PALETTE_OPTIONS: ReadonlyArray<IColourPaletteOption> = [
    ...STATIC_COLOUR_PALETTE_OPTIONS,
    ...AUTOMATIC_COLOUR_PALETTE_OPTIONS,
];

const COLOUR_PALETTE_IDS: ReadonlySet<string> = new Set(COLOUR_PALETTE_OPTIONS.map(option => option.id));

export function isColourPaletteId (paletteId: unknown): paletteId is ColourPaletteId {
    return typeof paletteId === 'string' && COLOUR_PALETTE_IDS.has(paletteId);
}

export function isConcreteColourPaletteId (paletteId: unknown): paletteId is ConcreteColourPaletteId {
    return typeof paletteId === 'string' && paletteId in cssVariablesJson.theme;
}

export function isTimeSensitiveAutomaticColourPaletteId (paletteId: unknown): paletteId is AutomaticColourPaletteId {
    return typeof paletteId === 'string' && TIME_SENSITIVE_AUTOMATIC_PALETTE_IDS.has(paletteId);
}

export function getFallbackColourPaletteId (isDarkMode: boolean): ColourPaletteId {
    return isDarkMode ? 'dark' : 'light';
}

export function resolveColourPaletteId (paletteId: unknown, isDarkMode = true): ColourPaletteId {
    if (isColourPaletteId(paletteId)) return paletteId;
    return getFallbackColourPaletteId(isDarkMode);
}

export function getSystemPrefersDarkMode (isDarkModeFallback = true): boolean {
    try {
        if (typeof window === 'undefined' || !window.matchMedia) return isDarkModeFallback;
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch {
        return isDarkModeFallback;
    }
}

function getLocalHour (date: Date): number {
    return date.getHours() + (date.getMinutes() / 60);
}

export function getDayWarmthAmount (date = new Date()): number {
    const hour = getLocalHour(date);

    if (hour < 6) return 1;
    if (hour < 8) return (8 - hour) / 2;
    if (hour < 14) return 0;
    if (hour < 21) return (hour - 14) / 7;
    return 1;
}

export function isEveningDarkActive (date = new Date()): boolean {
    const hour = getLocalHour(date);
    return hour < 6 || hour >= 18;
}

function isHexColour (colour: string): boolean {
    return /^#[\da-f]{6}$/i.test(colour);
}

function mixHexColour (startColour: string, endColour: string, amount: number): string {
    const startHex = startColour.replace('#', '');
    const endHex = endColour.replace('#', '');
    const channels = [0, 2, 4].map((index) => {
        const startValue = Number.parseInt(startHex.slice(index, index + 2), 16);
        const endValue = Number.parseInt(endHex.slice(index, index + 2), 16);
        const mixedValue = Math.round(startValue + ((endValue - startValue) * amount));
        return mixedValue.toString(16).padStart(2, '0');
    });

    return `#${channels.join('')}`.toUpperCase();
}

function isRecord (value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function mixThemeValue (lightValue: unknown, warmValue: unknown, amount: number): unknown {
    if (typeof lightValue === 'string' && typeof warmValue === 'string') {
        if (isHexColour(lightValue) && isHexColour(warmValue)) {
            return mixHexColour(lightValue, warmValue, amount);
        }

        return amount >= 0.5 ? warmValue : lightValue;
    }

    if (isRecord(lightValue) && isRecord(warmValue)) {
        return Object.fromEntries(
            Object.entries(lightValue).map(([key, value]) => [
                key,
                mixThemeValue(value, warmValue[key], amount),
            ]),
        );
    }

    return amount >= 0.5 ? warmValue : lightValue;
}

export function getDayWarmthThemeVariables (date = new Date()): ThemeVariables {
    const themeVariables = mixThemeValue(
        cssVariablesJson.theme.light,
        cssVariablesJson.theme.warmLight,
        getDayWarmthAmount(date),
    ) as ThemeVariables;

    themeVariables.colours.primary.text = cssVariablesJson.theme.warmLight.colours.primary.text;
    themeVariables.colours.secondary.text = cssVariablesJson.theme.light.colours.secondary.text;

    return themeVariables;
}

export function getEveningDarkConcreteColourPaletteId (date = new Date()): ConcreteColourPaletteId {
    return isEveningDarkActive(date) ? 'dark' : 'warmLight';
}

export function getActiveConcreteColourPaletteId (
    paletteId: unknown,
    isDarkModeFallback = true,
    systemPrefersDarkMode = getSystemPrefersDarkMode(isDarkModeFallback),
    date = new Date(),
): ConcreteColourPaletteId {
    const resolvedColourPaletteId = resolveColourPaletteId(paletteId, isDarkModeFallback);

    if (isConcreteColourPaletteId(resolvedColourPaletteId)) return resolvedColourPaletteId;

    switch (resolvedColourPaletteId) {
        case SYSTEM_PREFERENCE_COLOUR_PALETTE_ID:
            return getFallbackColourPaletteId(systemPrefersDarkMode) as ConcreteColourPaletteId;
        case 'eveningDark':
            return getEveningDarkConcreteColourPaletteId(date);
        case 'dayWarmth':
        default:
            return 'warmLight';
    }
}

export function getActiveColourPaletteMode (
    paletteId: unknown,
    isDarkModeFallback = true,
    systemPrefersDarkMode = getSystemPrefersDarkMode(isDarkModeFallback),
    date = new Date(),
): ColourPaletteMode {
    const activeConcreteColourPaletteId = getActiveConcreteColourPaletteId(
        paletteId,
        isDarkModeFallback,
        systemPrefersDarkMode,
        date,
    );

    return CONCRETE_COLOUR_PALETTE_MODE_BY_ID[activeConcreteColourPaletteId];
}

export function getThemeVariablesForPalette (
    paletteId: unknown,
    isDarkModeFallback = true,
    date = new Date(),
    systemPrefersDarkMode = getSystemPrefersDarkMode(isDarkModeFallback),
): ThemeVariables {
    const resolvedColourPaletteId = resolveColourPaletteId(paletteId, isDarkModeFallback);

    if (resolvedColourPaletteId === 'dayWarmth') {
        return getDayWarmthThemeVariables(date);
    }

    if (resolvedColourPaletteId === 'eveningDark' && !isEveningDarkActive(date)) {
        return getDayWarmthThemeVariables(date);
    }

    const activeConcreteColourPaletteId = getActiveConcreteColourPaletteId(
        resolvedColourPaletteId,
        isDarkModeFallback,
        systemPrefersDarkMode,
        date,
    );

    return cssVariablesJson.theme[activeConcreteColourPaletteId];
}

export function getStoredColourPaletteId (): ColourPaletteId | null {
    try {
        if (typeof window === 'undefined') return null;
        const storedPaletteId = window.localStorage.getItem(COLOUR_PALETTE_STORAGE_KEY);
        return isColourPaletteId(storedPaletteId) ? storedPaletteId : null;
    } catch {
        return null;
    }
}

export function setStoredColourPaletteId (paletteId: ColourPaletteId): void {
    try {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(COLOUR_PALETTE_STORAGE_KEY, paletteId);
    } catch {
        // localStorage can be unavailable in private browsing or locked-down embeds.
    }
}
